/**
 * Purchase State Module
 *
 * This module re-exports purchase state types and functions from the shared package
 * and provides app-specific configuration and helpers.
 */

import { APP_CONFIG, APP_TO_BUILD, ENV } from "../../appsConfig";
import {
  APP_PURCHASE_STATE,
  getPurchaseAccess as sharedGetPurchaseAccess,
  getPurchaseAccessFromOldPurchaseState as sharedGetPurchaseAccessFromOldPurchaseState,
  mergePurchaseAccesses as sharedMergePurchaseAccesses,
  noAccess as sharedNoAccess,
  forcePurchaseAccessToPurchaseAccess as sharedForcePurchaseAccessToPurchaseAccess,
  getUserAccessLabel as sharedGetUserAccessLabel,
  PurchaseConfig,
  PurchaseAccess,
  PurchasedProduct,
  NewPurchaseState,
  ForcePurchaseAccess,
  UserAccessLabel,
  Environment,
} from "@petergok/blue-collar-shared";
import { buildProModeSkus } from "./buildProModeSkus";

// Re-export types from shared package
export { APP_PURCHASE_STATE };
export type {
  PurchaseAccess,
  PurchasedProduct,
  NewPurchaseState,
  ForcePurchaseAccess,
  UserAccessLabel,
};

// App-specific purchase configuration
const PURCHASE_CONFIG: PurchaseConfig = {
  appToBuild: APP_TO_BUILD,
  proModeSkus: buildProModeSkus(APP_CONFIG.proModeSkus),
  environment: ENV as Environment,
};

/**
 * Wrapper function that calls the shared getPurchaseAccess with app config
 */
export const getPurchaseAccess = (
  purchasedProducts: NewPurchaseState["purchasedProducts"],
  source: string
): PurchaseAccess => {
  return sharedGetPurchaseAccess(purchasedProducts, source, PURCHASE_CONFIG);
};

/**
 * Wrapper function that calls the shared getPurchaseAccessFromOldPurchaseState
 */
export const getPurchaseAccessFromOldPurchaseState = (
  purchaseState: APP_PURCHASE_STATE,
  source: string
): PurchaseAccess => {
  return sharedGetPurchaseAccessFromOldPurchaseState(purchaseState, source);
};

/**
 * Wrapper function that calls the shared mergePurchaseAccesses
 */
export const mergePurchaseAccesses = (
  purchaseAccesses: PurchaseAccess[]
): PurchaseAccess => {
  return sharedMergePurchaseAccesses(purchaseAccesses);
};

/**
 * Wrapper function that calls the shared noAccess
 */
export const noAccess = (purchaseAccess: PurchaseAccess): boolean => {
  return sharedNoAccess(purchaseAccess);
};

/**
 * Wrapper function that calls the shared forcePurchaseAccessToPurchaseAccess
 */
export const forcePurchaseAccessToPurchaseAccess = (forcePurchaseAccess: {
  aiAccess: boolean;
  studyAccess: boolean;
  allLevelStudyAccess: boolean;
}): PurchaseAccess => {
  return sharedForcePurchaseAccessToPurchaseAccess(forcePurchaseAccess);
};

/**
 * Wrapper function that calls the shared getUserAccessLabel with app config
 */
export const getUserAccessLabel = (purchaseAccess: PurchaseAccess): string => {
  return sharedGetUserAccessLabel(purchaseAccess, APP_TO_BUILD);
};
